import { useQuery } from '@tanstack/react-query'

import type {
    CareLogReportParams,
    CashbookReportParams,
    DateRangeParams,
    DebtPeriodParams,
    InventoryDetailsParams,
    InventoryFilterParams,
    LotStockKind,
    ReportPaymentMethod,
    SalesReportParams,
    StockOutOrdersParams,
    YieldLossNotesArgs,
    YieldLossParams,
} from '@/lib/api/report-api'
import { useAuth } from '@/hooks/use-auth'
import { reportApi } from '@/lib/api/report-api'

export const reportKeys = {
    revenueSummary: (params: DateRangeParams) =>
        ['reports', 'revenue-summary', params] as const,
    revenueByTime: (params: DateRangeParams & { group_by?: string }) =>
        ['reports', 'revenue-by-time', params] as const,
    revenueByProduct: (params: DateRangeParams & { limit?: number }) =>
        ['reports', 'revenue-by-product', params] as const,
    revenueByCustomer: (params: DateRangeParams & { limit?: number }) =>
        ['reports', 'revenue-by-customer', params] as const,
    revenueByChannel: (params: DateRangeParams) =>
        ['reports', 'revenue-by-channel', params] as const,
    revenueByProvince: (params: DateRangeParams & { limit?: number }) =>
        ['reports', 'revenue-by-province', params] as const,
    ordersByProvince: (params: DateRangeParams & { province_code: string }) =>
        ['reports', 'orders-by-province', params] as const,
    revenueByWard: (
        params: DateRangeParams & { province_code: string; limit?: number },
    ) => ['reports', 'revenue-by-ward', params] as const,
    ordersSummary: (params: DateRangeParams) =>
        ['reports', 'orders-summary', params] as const,
    debtSummary: (params: DateRangeParams) =>
        ['reports', 'debt-summary', params] as const,
    cashFlow: (params: DateRangeParams) =>
        ['reports', 'cash-flow', params] as const,
    compareRevenue: (params: DateRangeParams) =>
        ['reports', 'revenue-compare', params] as const,
    positionStockSummary: () => ['reports', 'position-stock-summary'] as const,
    lotStock: (kind: LotStockKind, params: LotStockParams) =>
        ['reports', 'lot-stock', kind, params] as const,
    lotGradeNotes: (
        kind: LotStockKind,
        gradeId: string,
        params: DateRangeParams,
    ) => ['reports', 'lot-grade-notes', kind, gradeId, params] as const,
    careLogSummary: (params: CareLogReportParams) =>
        ['reports', 'care-log-summary', params] as const,
    careLogGrowerLogs: (growerId: string, params: CareLogReportParams) =>
        ['reports', 'care-log-grower-logs', growerId, params] as const,
    careLogGrowerAreas: (growerId: string) =>
        ['reports', 'care-log-grower-areas', growerId] as const,
    growers: () => ['reports', 'growers'] as const,
    positionStock: (id: string) => ['reports', 'position-stock', id] as const,
    cashbookByDay: (params: CashbookReportParams) =>
        ['reports', 'cashbook-by-day', params] as const,
    cashbookTransactions: (params: CashbookReportParams) =>
        ['reports', 'cashbook-transactions', params] as const,
    salesByDay: (params: SalesReportParams) =>
        ['reports', 'sales-by-day', params] as const,
    salesTransactions: (params: SalesReportParams) =>
        ['reports', 'sales-transactions', params] as const,
    stockOutByProduct: (params: DateRangeParams) =>
        ['reports', 'stock-out-by-product', params] as const,
    stockOutOrders: (params: StockOutOrdersParams) =>
        ['reports', 'stock-out-orders', params] as const,
    debtPeriod: (params: DebtPeriodParams) =>
        ['reports', 'debt-period', params] as const,
    debtLedgerPeriod: (customerId: string, params: DateRangeParams) =>
        ['reports', 'debt-ledger-period', customerId, params] as const,
    inventorySummary: (params: InventoryFilterParams = {}) =>
        ['reports', 'inventory-summary', params] as const,
    inventoryPositions: (productId: string) =>
        ['reports', 'inventory-positions', productId] as const,
    yieldLoss: (params: YieldLossParams) =>
        ['reports', 'yield-loss', params] as const,
    yieldLossNotes: (args: YieldLossNotesArgs | null) =>
        ['reports', 'yield-loss-notes', args] as const,
    inventoryDetails: (params: InventoryDetailsParams) =>
        ['reports', 'inventory-details', params] as const,
    staffPerformance: (params: DateRangeParams) =>
        ['reports', 'staff-performance', params] as const,
    deliveryPerformance: (params: DateRangeParams) =>
        ['reports', 'delivery-performance', params] as const,
    driverPerformance: (params: DateRangeParams) =>
        ['reports', 'driver-performance', params] as const,
    movementByTime: (params: DateRangeParams & { group_by?: string }) =>
        ['reports', 'movement-by-time', params] as const,
}

export function useRevenueSummary(params?: DateRangeParams) {
    return useQuery({
        queryKey: reportKeys.revenueSummary(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.revenueSummary(params)
            return res.data ?? null
        },
    })
}

export function useRevenueByTime(
    params?: DateRangeParams & { group_by?: 'day' | 'week' | 'month' },
) {
    return useQuery({
        queryKey: reportKeys.revenueByTime(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.revenueByTime(params)
            return res.data ?? []
        },
    })
}

export function useRevenueByProduct(
    params?: DateRangeParams & { limit?: number },
) {
    return useQuery({
        queryKey: reportKeys.revenueByProduct(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.revenueByProduct(params)
            return res.data ?? []
        },
    })
}

export function useRevenueByCustomer(
    params?: DateRangeParams & { limit?: number },
) {
    return useQuery({
        queryKey: reportKeys.revenueByCustomer(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.revenueByCustomer(params)
            return res.data ?? []
        },
    })
}

export function useRevenueByChannel(params?: DateRangeParams) {
    return useQuery({
        queryKey: reportKeys.revenueByChannel(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.revenueByChannel(params)
            return res.data ?? []
        },
    })
}

export function useRevenueByProvince(
    params?: DateRangeParams & { limit?: number },
) {
    return useQuery({
        queryKey: reportKeys.revenueByProvince(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.revenueByProvince(params)
            return res.data ?? []
        },
    })
}

// useOrdersByProvince drives the province drill-down. It only fires once a
// province is selected (enabled), so the report shows the pie alone until the
// user clicks a slice.
export function useOrdersByProvince(
    params: (DateRangeParams & { province_code: string }) | null,
) {
    return useQuery({
        queryKey: reportKeys.ordersByProvince(
            params ?? { province_code: '__none__' },
        ),
        queryFn: async () => {
            const res = await reportApi.ordersByProvince(params!)
            return res.data ?? []
        },
        enabled: params !== null,
    })
}

// useRevenueByWard drives the per-province ward breakdown. It only fires once a
// province is selected (enabled), so the report shows the province pie alone
// until the user drills into a province.
export function useRevenueByWard(
    params: (DateRangeParams & { province_code: string }) | null,
) {
    return useQuery({
        queryKey: reportKeys.revenueByWard(
            params ?? { province_code: '__none__' },
        ),
        queryFn: async () => {
            const res = await reportApi.revenueByWard(params!)
            return res.data ?? []
        },
        enabled: params !== null,
    })
}

export function useOrdersSummary(params?: DateRangeParams) {
    return useQuery({
        queryKey: reportKeys.ordersSummary(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.ordersSummary(params)
            return res.data ?? null
        },
    })
}

export function useDebtSummary(params?: DateRangeParams) {
    return useQuery({
        queryKey: reportKeys.debtSummary(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.debtSummary(params)
            return res.data ?? null
        },
    })
}

export function useCashFlow(
    params?: DateRangeParams & { payment_method?: ReportPaymentMethod },
) {
    return useQuery({
        queryKey: reportKeys.cashFlow(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.cashFlow(params)
            return res.data ?? null
        },
    })
}

export function useCompareRevenueSummary(params?: DateRangeParams) {
    return useQuery({
        queryKey: reportKeys.compareRevenue(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.compareRevenueSummary(params)
            return res.data ?? null
        },
    })
}

// Báo cáo tồn để KIỂM tồn: phiếu nhập/xuất/chuyển ở màn khác không làm mới
// các key này, nên staleTime 0 như màn Vị trí kho — mở lại là số mới.
export function usePositionStockSummary() {
    return useQuery({
        queryKey: reportKeys.positionStockSummary(),
        staleTime: 0,
        queryFn: async () => {
            const res = await reportApi.positionStockSummary()
            return res.data?.rows ?? null
        },
    })
}

/** `positionId` rỗng = chưa mở tầng chi tiết → không gọi API. */
export function usePositionStock(positionId: string) {
    return useQuery({
        queryKey: reportKeys.positionStock(positionId),
        staleTime: 0,
        enabled: positionId !== '',
        queryFn: async () => {
            const res = await reportApi.positionStock(positionId)
            return res.data ?? null
        },
    })
}

export interface LotStockParams {
    from: string
    to: string
    group_by: string
}

export function useLotStock(kind: LotStockKind, params: LotStockParams) {
    return useQuery({
        queryKey: reportKeys.lotStock(kind, params),
        staleTime: 0,
        queryFn: async () => {
            const res = await reportApi.lotStock(kind, params)
            return res.data ?? null
        },
    })
}

/** `gradeId` rỗng = chưa mở tầng chi tiết → không gọi API. */
export function useLotGradeNotes(
    kind: LotStockKind,
    gradeId: string,
    params: DateRangeParams,
) {
    return useQuery({
        queryKey: reportKeys.lotGradeNotes(kind, gradeId, params),
        staleTime: 0,
        enabled: gradeId !== '',
        queryFn: async () => {
            const res = await reportApi.lotGradeNotes(kind, gradeId, params)
            return res.data ?? null
        },
    })
}

export function useCareLogSummary(params: CareLogReportParams) {
    return useQuery({
        queryKey: reportKeys.careLogSummary(params),
        staleTime: 0,
        queryFn: async () => {
            const res = await reportApi.careLogSummary(params)
            return res.data?.rows ?? null
        },
    })
}

/** `growerId` rỗng = chưa mở tầng chi tiết → không gọi API. */
export function useCareLogGrowerLogs(
    growerId: string,
    params: CareLogReportParams,
) {
    return useQuery({
        queryKey: reportKeys.careLogGrowerLogs(growerId, params),
        staleTime: 0,
        enabled: growerId !== '',
        queryFn: async () => {
            const res = await reportApi.careLogGrowerLogs(growerId, params)
            return res.data ?? null
        },
    })
}

/** `growerId` rỗng = chưa mở tầng chi tiết → không gọi API. */
export function useCareLogGrowerAreas(growerId: string) {
    return useQuery({
        queryKey: reportKeys.careLogGrowerAreas(growerId),
        enabled: growerId !== '',
        queryFn: async () => {
            const res = await reportApi.careLogGrowerAreas(growerId)
            return res.data ?? []
        },
    })
}

export function useGrowerReport() {
    return useQuery({
        queryKey: reportKeys.growers(),
        staleTime: 0,
        queryFn: async () => {
            const res = await reportApi.growers()
            return res.data?.rows ?? null
        },
    })
}

export function useCashbookByDay(params: CashbookReportParams) {
    return useQuery({
        queryKey: reportKeys.cashbookByDay(params),
        queryFn: async () => {
            const res = await reportApi.cashbookByDay(params)
            return res.data ?? null
        },
    })
}

/** `enabled` false = chưa mở tầng phiếu → không gọi API. */
export function useCashbookTransactions(
    params: CashbookReportParams,
    enabled: boolean,
) {
    return useQuery({
        queryKey: reportKeys.cashbookTransactions(params),
        queryFn: async () => {
            const res = await reportApi.cashbookTransactions(params)
            return res.data ?? null
        },
        enabled,
    })
}

export function useSalesByDay(params: SalesReportParams) {
    return useQuery({
        queryKey: reportKeys.salesByDay(params),
        queryFn: async () => {
            const res = await reportApi.salesByDay(params)
            return res.data ?? null
        },
    })
}

/** `enabled` false = chưa mở tầng chứng từ → không gọi API. */
export function useSalesTransactions(
    params: SalesReportParams,
    enabled: boolean,
) {
    return useQuery({
        queryKey: reportKeys.salesTransactions(params),
        queryFn: async () => {
            const res = await reportApi.salesTransactions(params)
            return res.data ?? null
        },
        enabled,
    })
}

export function useStockOutByProduct(params: DateRangeParams) {
    return useQuery({
        queryKey: reportKeys.stockOutByProduct(params),
        queryFn: async () => {
            const res = await reportApi.stockOutByProduct(params)
            return res.data ?? null
        },
    })
}

/** `enabled` false = chưa bấm vào sản phẩm → không gọi API. */
export function useStockOutOrders(
    params: StockOutOrdersParams,
    enabled: boolean,
) {
    return useQuery({
        queryKey: reportKeys.stockOutOrders(params),
        queryFn: async () => {
            const res = await reportApi.stockOutOrders(params)
            return res.data ?? null
        },
        enabled,
    })
}

export function useDebtByCustomerPeriod(params: DebtPeriodParams) {
    return useQuery({
        queryKey: reportKeys.debtPeriod(params),
        queryFn: async () => {
            const res = await reportApi.debtByCustomerPeriod(params)
            return res.data ?? null
        },
    })
}

/** `customerId` rỗng = chưa mở tầng sổ của khách nào → không gọi API. */
export function useCustomerDebtLedgerPeriod(
    customerId: string,
    params: DateRangeParams,
) {
    return useQuery({
        queryKey: reportKeys.debtLedgerPeriod(customerId, params),
        queryFn: async () => {
            const res = await reportApi.customerDebtLedgerPeriod(
                customerId,
                params,
            )
            return res.data ?? null
        },
        enabled: customerId !== '',
    })
}

export function useInventorySummary(params?: InventoryFilterParams) {
    return useQuery({
        queryKey: reportKeys.inventorySummary(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.inventorySummary(params)
            return res.data ?? null
        },
    })
}

export function useInventoryDetails(params?: InventoryDetailsParams) {
    return useQuery({
        queryKey: reportKeys.inventoryDetails(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.inventoryDetails(params)
            return res.data ?? []
        },
    })
}

/** Tồn theo vị trí của một sản phẩm; `productId` rỗng = chưa bung dòng nào nên
 * không gọi API. */
export function useInventoryPositions(productId: string) {
    return useQuery({
        queryKey: reportKeys.inventoryPositions(productId),
        queryFn: async () => {
            const res = await reportApi.inventoryPositions(productId)
            return res.data ?? []
        },
        enabled: !!productId,
    })
}

export function useYieldLoss(params: YieldLossParams) {
    return useQuery({
        queryKey: reportKeys.yieldLoss(params),
        queryFn: async () => {
            const res = await reportApi.yieldLoss(params)
            return res.data ?? null
        },
    })
}

/** `args` null = chưa mở popup nào nên không gọi API. */
export function useYieldLossNotes(args: YieldLossNotesArgs | null) {
    return useQuery({
        queryKey: reportKeys.yieldLossNotes(args),
        queryFn: async () => {
            if (!args) return null
            const res = await reportApi.yieldLossNotes(args)
            return res.data ?? null
        },
        enabled: !!args,
    })
}

export function useStaffPerformance(params?: DateRangeParams) {
    return useQuery({
        queryKey: reportKeys.staffPerformance(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.staffPerformance(params)
            return res.data ?? []
        },
    })
}

export function useDeliveryPerformance(params?: DateRangeParams) {
    return useQuery({
        queryKey: reportKeys.deliveryPerformance(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.deliveryPerformance(params)
            return res.data ?? null
        },
    })
}

export function useDriverPerformance(params?: DateRangeParams) {
    return useQuery({
        queryKey: reportKeys.driverPerformance(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.driverPerformance(params)
            return res.data ?? []
        },
    })
}

export function useDefects(
    params?: DateRangeParams & {
        group_by?: 'day' | 'week' | 'month' | 'year'
    },
) {
    return useQuery({
        queryKey: ['reports', 'inventory-defects', params ?? {}] as const,
        queryFn: async () => {
            const res = await reportApi.defects(params)
            return res.data ?? []
        },
    })
}

export function useMovementByTime(
    params?: DateRangeParams & { group_by?: 'day' | 'week' | 'month' | 'year' },
) {
    const { hasPermission, arePermissionsLoaded } = useAuth()
    return useQuery({
        queryKey: reportKeys.movementByTime(params ?? {}),
        queryFn: async () => {
            const res = await reportApi.movementByTime(params)
            return res.data ?? []
        },
        enabled: arePermissionsLoaded && hasPermission('reports.read'),
    })
}
